// Auth error detection utilities for handling session expiry gracefully

/**
 * Patterns that indicate an authentication/session error
 * These should trigger logout rather than showing a form validation error
 */
const AUTH_ERROR_PATTERNS = [
  'missing refresh token',
  'refresh token',
  'access token',
  'token expired',
  'session expired',
  'unauthorized',
  'authentication required',
  'invalid token',
  'jwt expired',
  'jwt malformed',
  'no authorization',
  'login required',
  '401',
  'unauthenticated'
];

/**
 * HTTP status codes that indicate authentication failure
 */
const AUTH_ERROR_STATUS_CODES = [401, 403];

/**
 * Check if an error is related to authentication/session
 * @param {Error|Object|string} error - The error to check
 * @returns {boolean} - True if this is an auth-related error
 */
export const isAuthError = (error) => {
  if (!error) return false;
  
  // Check error message
  const message = typeof error === 'string' 
    ? error.toLowerCase()
    : (error.message || error.error || '').toLowerCase();
  
  // Check against known auth error patterns
  const matchesPattern = AUTH_ERROR_PATTERNS.some(pattern => 
    message.includes(pattern)
  );
  
  if (matchesPattern) return true;
  
  // Check HTTP status code
  const status = error.status || error.statusCode || error.response?.status;
  if (AUTH_ERROR_STATUS_CODES.includes(status)) return true;
  
  // Check for Auth0-specific error codes
  if (error.error === 'login_required' || 
      error.error === 'consent_required' ||
      error.error === 'interaction_required') {
    return true;
  }
  
  return false;
};

/**
 * Get a user-friendly message for auth errors
 * @param {Error|Object|string} error - The error to check  
 * @returns {string} - User-friendly message
 */
export const getAuthErrorMessage = (error) => {
  if (!error) return 'Your session has expired. Please log in again.';
  
  const message = typeof error === 'string' 
    ? error.toLowerCase()
    : (error.message || error.error || '').toLowerCase();
  
  if (message.includes('refresh token')) {
    return 'Your session has expired. Please log in again to continue.';
  }
  
  if (message.includes('token expired') || message.includes('jwt expired')) {
    return 'Your authentication token has expired. Please log in again.';
  }
  
  if (message.includes('unauthorized') || message.includes('401')) {
    return 'You are not authorized. Please log in again.';
  }
  
  return 'Your session has expired. Please log in again.';
};

/**
 * Handle an auth error by triggering logout
 * @param {Function} logout - Auth0 logout function
 * @param {string} returnTo - Optional URL to return to after login (defaults to current origin)
 */
export const handleAuthError = (logout, returnTo = null) => {
  if (!logout) {
    return;
  }
  
  // Always use window.location.origin to stay on the current environment
  const returnUrl = returnTo || window.location.origin;
  
  logout({
    logoutParams: {
      returnTo: returnUrl
    }
  });
};

export default {
  isAuthError,
  getAuthErrorMessage,
  handleAuthError
};
